import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { useMobileDetect } from '@/hooks';
import type { UserProfile } from '@types';
import { UnityGameModal, SoldierUpgradeModal } from '@/components/modals';
import type { SoldierType } from '@/components/modals/SoldierUpgradeModal';
import type { GameMode } from '@/components/modals/UnityGameModal';
import { useDefenderStore } from '@app/stores/defenderStore';
import { defenderService } from '@services';
import characterContainer from '@/assets/home/components/character-container.webp';
import pveButton from '@/assets/home/components/pve-button.webp';
import pvpButton from '@/assets/home/components/pvp-button.webp';

// Soldier images
import archerImg from '@/assets/home/components/soliders/archer.webp';
import fireMageImg from '@/assets/home/components/soliders/fire-mage.webp';
import iceMageImg from '@/assets/home/components/soliders/ice-mage.webp';
import gunnerImg from '@/assets/home/components/soliders/gunner.webp';

interface BattleContentProps {
  userProfile: UserProfile;
  onRefreshContent?: () => void;
}

export const BattleContent = ({
  userProfile,
  onRefreshContent,
}: BattleContentProps) => {
  const isMobile = useMobileDetect();
  const [bottomMargin, setBottomMargin] = useState(96);
  const [initialViewport, setInitialViewport] = useState(0);
  const [isUnityGameOpen, setIsUnityGameOpen] = useState(false);
  const [selectedGameMode, setSelectedGameMode] = useState<GameMode>('pve');
  const [imagesLoaded, setImagesLoaded] = useState(false);

  // Soldier upgrade modal state
  const [isSoldierModalOpen, setIsSoldierModalOpen] = useState(false);
  const [selectedSoldier, setSelectedSoldier] = useState<SoldierType>('archer');

  // Hover states for each unit to control animations
  const [archerHover, setArcherHover] = useState(false);
  const [fireMageHover, setFireMageHover] = useState(false);
  const [iceMageHover, setIceMageHover] = useState(false);
  const [gunnerHover, setGunnerHover] = useState(false);

  // Defender store
  const {
    setDefenderTypes,
    setUserDefenders,
    setLoadingTypes,
    setLoadingUserDefenders,
  } = useDefenderStore();

  // Fetch defender data on mount
  useEffect(() => {
    const fetchDefenderData = async () => {
      try {
        // Fetch defender types (base stats)
        setLoadingTypes(true);
        const types = await defenderService.getDefenderTypes();
        setDefenderTypes(types);
      } catch (error) {
        console.error('Failed to fetch defender types:', error);
      } finally {
        setLoadingTypes(false);
      }

      try {
        // Fetch user defenders (progress)
        setLoadingUserDefenders(true);
        const userDefenders = await defenderService.getUserDefenders();
        // Only take first 4 defenders (temporary fix for BE bug)
        setUserDefenders(userDefenders.slice(0, 4));
      } catch (error) {
        console.error('Failed to fetch user defenders:', error);
      } finally {
        setLoadingUserDefenders(false);
      }
    };

    fetchDefenderData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const imagesToPreload = [
      characterContainer,
      archerImg,
      fireMageImg,
      iceMageImg,
      gunnerImg,
      pveButton,
      pvpButton,
    ];

    let loadedCount = 0;
    const totalImages = imagesToPreload.length;

    imagesToPreload.forEach((src) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(() => setImagesLoaded(true), 200);
            });
          });
        }
      };
      img.onerror = () => {
        loadedCount++;
        if (loadedCount === totalImages) {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              setTimeout(() => setImagesLoaded(true), 200);
            });
          });
        }
      };
    });

    const fallbackTimer = setTimeout(() => setImagesLoaded(true), 1500);
    return () => clearTimeout(fallbackTimer);
  }, []);

  useEffect(() => {
    const updateMargin = () => {
      const navbar = document.querySelector('nav');
      if (navbar) {
        const navbarHeight = navbar.offsetHeight;

        if (!isMobile) {
          setBottomMargin(navbarHeight * 1.0);
          return;
        }

        const currentViewport = window.innerHeight;

        if (initialViewport === 0) {
          setInitialViewport(currentViewport);
          setBottomMargin(navbarHeight * 1.0);
          return;
        }

        const ratio = initialViewport / currentViewport;
        const scaledMargin = navbarHeight * ratio * 1.5;
        setBottomMargin(scaledMargin);
      }
    };

    updateMargin();

    window.addEventListener('resize', updateMargin);
    window.addEventListener('orientationchange', updateMargin);

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', updateMargin);
    }

    return () => {
      window.removeEventListener('resize', updateMargin);
      window.removeEventListener('orientationchange', updateMargin);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', updateMargin);
      }
    };
  }, [initialViewport, isMobile]);

  const handlePveClick = () => {
    setSelectedGameMode('pve');
    setIsUnityGameOpen(true);
  };

  const handlePvpClick = () => {
    if (!userProfile.pvp_unlocked) return;
    setSelectedGameMode('pvp');
    setIsUnityGameOpen(true);
  };

  const handleSoldierClick = (soldierType: SoldierType) => {
    setSelectedSoldier(soldierType);
    setIsSoldierModalOpen(true);
  };

  const handleSoldierUpgrade = (soldierType: SoldierType) => {
    console.log('[BattleContent] Upgrading soldier:', soldierType);
    // Upgrade will be handled by SoldierUpgradeModal
  };

  // Refetch defender data after successful upgrade
  const refetchDefenderData = async () => {
    console.log('[BattleContent] Refreshing defender data after upgrade...');
    try {
      const userDefenders = await defenderService.getUserDefenders();
      console.log(
        '[BattleContent] Fresh data from API:',
        userDefenders.slice(0, 4)
      );
      setUserDefenders(userDefenders.slice(0, 4));
      console.log('[BattleContent] ✅ Defender data refreshed and stored');
    } catch (error) {
      console.error('[BattleContent] Failed to refresh defender data:', error);
    }
  };

  return (
    <div
      className={`w-full h-full flex flex-col items-center justify-end relative sm:pt-[20%] sm:pb-[${bottomMargin}]`}
    >
      <motion.div
        className="flex flex-col w-full px-5"
        style={{ gap: '16px', marginBottom: `${bottomMargin}px` }}
        initial={{ opacity: 0, y: 30 }}
        animate={imagesLoaded ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
      >
        {/* Character Container */}
        <motion.div
          className="w-full relative"
          initial={{ scale: 0.8, opacity: 0 }}
          animate={
            imagesLoaded
              ? {
                  scale: 1,
                  opacity: 1,
                  y: [0, -8, 0],
                }
              : { scale: 0.8, opacity: 0 }
          }
          transition={{
            scale: { duration: 0.5, ease: 'easeOut' },
            opacity: { duration: 0.5 },
            // y: {
            //   duration: 3,
            //   repeat: Infinity,
            //   ease: 'easeInOut',
            // },
          }}
        >
          <motion.img
            src={characterContainer}
            alt="Character Container"
            className="w-full h-full object-contain"
            whileHover={{
              scale: 1.02,
              filter: 'brightness(1.1)',
            }}
            transition={{ duration: 0.3 }}
          />
          {/* Units Row */}
          <motion.div
            className="absolute flex flex-row items-center gap-2 bottom-6 left-5 right-5"
            initial="hidden"
            animate={imagesLoaded ? 'visible' : 'hidden'}
            variants={{
              hidden: { opacity: 0 },
              visible: {
                opacity: 1,
                transition: {
                  staggerChildren: 0.15,
                  delayChildren: 0.3,
                },
              },
            }}
          >
            {/* Character 1 - Archer */}
            <motion.div
              className="relative flex-1 aspect-square overflow-hidden cursor-pointer"
              onClick={() => handleSoldierClick('archer')}
              style={{
                background: 'rgba(16, 255, 160, 0.2)',
                border: '2px solid rgba(0, 0, 0, 0.7)',
                boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(2px)',
                borderRadius: '12px',
              }}
              variants={{
                hidden: { scale: 0, opacity: 0, y: 20 },
                visible: {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  transition: {
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                  },
                },
              }}
              whileHover={{
                scale: 1.05,
                y: -5,
                boxShadow: '0 8px 20px rgba(16, 255, 160, 0.6)',
                borderColor: 'rgba(16, 255, 160, 0.8)',
                transition: { duration: 0.15, ease: 'easeOut' },
              }}
              whileTap={{
                scale: 0.95,
                transition: { duration: 0.15 },
              }}
            >
              <motion.img
                src={archerImg}
                alt="Archer"
                className="absolute inset-0 w-full h-full object-cover"
                onHoverStart={() => setArcherHover(true)}
                onHoverEnd={() => setArcherHover(false)}
                animate={
                  archerHover
                    ? {
                        scale: 1.1,
                        rotate: [0, -2, 2, -2, 0, -2, 2, -2, 0],
                        y: [0, -2, 0, -2, 0],
                        filter: 'brightness(1.2)',
                      }
                    : {
                        scale: 1,
                        rotate: 0,
                        y: 0,
                        filter: 'brightness(1)',
                      }
                }
                transition={
                  archerHover
                    ? {
                        scale: { duration: 0.2 },
                        rotate: {
                          duration: 2,
                          ease: 'easeInOut',
                          repeat: Infinity,
                        },
                        y: {
                          duration: 1.5,
                          ease: 'easeInOut',
                          repeat: Infinity,
                        },
                        filter: { duration: 0.2 },
                      }
                    : {
                        duration: 0.3,
                      }
                }
              />
            </motion.div>

            {/* Character 2 - Fire Mage */}
            <motion.div
              className="relative flex-1 aspect-square overflow-hidden cursor-pointer"
              onClick={() => handleSoldierClick('fireMage')}
              style={{
                background: 'rgba(227, 24, 48, 0.2)',
                border: '2px solid rgba(0, 0, 0, 0.7)',
                boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(2px)',
                borderRadius: '12px',
              }}
              variants={{
                hidden: { scale: 0, opacity: 0, y: 20 },
                visible: {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  transition: {
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                  },
                },
              }}
              whileHover={{
                scale: 1.05,
                y: -5,
                boxShadow: '0 8px 20px rgba(227, 24, 48, 0.6)',
                borderColor: 'rgba(227, 24, 48, 0.8)',
                transition: { duration: 0.15, ease: 'easeOut' },
              }}
              whileTap={{
                scale: 0.95,
                transition: { duration: 0.15 },
              }}
            >
              <motion.img
                src={fireMageImg}
                alt="Fire Mage"
                className="absolute inset-0 w-full h-full object-cover"
                onHoverStart={() => setFireMageHover(true)}
                onHoverEnd={() => setFireMageHover(false)}
                animate={
                  fireMageHover
                    ? {
                        scale: 1.1,
                        y: [0, -3, 0, -3, 0, -3, 0],
                        filter: [
                          'brightness(1.3) saturate(1.2)',
                          'brightness(1.4) saturate(1.3)',
                          'brightness(1.3) saturate(1.2)',
                        ],
                      }
                    : {
                        scale: 1,
                        y: 0,
                        filter: 'brightness(1) saturate(1)',
                      }
                }
                transition={
                  fireMageHover
                    ? {
                        scale: { duration: 0.2 },
                        y: {
                          duration: 2.5,
                          ease: 'easeInOut',
                          repeat: Infinity,
                        },
                        filter: {
                          duration: 2,
                          ease: 'easeInOut',
                          repeat: Infinity,
                        },
                      }
                    : {
                        duration: 0.3,
                      }
                }
              />
            </motion.div>

            {/* Character 3 - Ice Mage */}
            <motion.div
              className="relative flex-1 aspect-square overflow-hidden cursor-pointer"
              onClick={() => handleSoldierClick('iceMage')}
              style={{
                background: 'rgba(36, 95, 255, 0.2)',
                border: '2px solid rgba(0, 0, 0, 0.7)',
                boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(2px)',
                borderRadius: '12px',
              }}
              variants={{
                hidden: { scale: 0, opacity: 0, y: 20 },
                visible: {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  transition: {
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                  },
                },
              }}
              whileHover={{
                scale: 1.05,
                y: -5,
                boxShadow: '0 8px 20px rgba(36, 95, 255, 0.6)',
                borderColor: 'rgba(36, 95, 255, 0.8)',
                transition: { duration: 0.15, ease: 'easeOut' },
              }}
              whileTap={{
                scale: 0.95,
                transition: { duration: 0.15 },
              }}
            >
              <motion.img
                src={iceMageImg}
                alt="Ice Mage"
                className="absolute inset-0 w-full h-full object-cover"
                onHoverStart={() => setIceMageHover(true)}
                onHoverEnd={() => setIceMageHover(false)}
                animate={
                  iceMageHover
                    ? {
                        scale: [1.1, 1.12, 1.1, 1.12, 1.1],
                        rotate: [0, 3, -3, 3, -3, 3, 0],
                        filter: 'brightness(1.25) saturate(1.3)',
                      }
                    : {
                        scale: 1,
                        rotate: 0,
                        filter: 'brightness(1) saturate(1)',
                      }
                }
                transition={
                  iceMageHover
                    ? {
                        scale: {
                          duration: 1.8,
                          ease: 'easeInOut',
                          repeat: Infinity,
                        },
                        rotate: {
                          duration: 2.2,
                          ease: 'easeInOut',
                          repeat: Infinity,
                        },
                        filter: { duration: 0.2 },
                      }
                    : {
                        duration: 0.3,
                      }
                }
              />
            </motion.div>

            {/* Character 4 - Gunner */}
            <motion.div
              className="relative flex-1 aspect-square overflow-hidden cursor-pointer"
              onClick={() => handleSoldierClick('gunner')}
              style={{
                background: 'rgba(0, 0, 0, 0.4)',
                border: '2px solid rgba(0, 0, 0, 0.7)',
                boxShadow: '4px 4px 4px rgba(0, 0, 0, 0.45)',
                backdropFilter: 'blur(2px)',
                borderRadius: '12px',
              }}
              variants={{
                hidden: { scale: 0, opacity: 0, y: 20 },
                visible: {
                  scale: 1,
                  opacity: 1,
                  y: 0,
                  transition: {
                    type: 'spring',
                    stiffness: 300,
                    damping: 15,
                  },
                },
              }}
              whileHover={{
                scale: 1.05,
                y: -5,
                boxShadow: '0 8px 20px rgba(255, 255, 255, 0.3)',
                borderColor: 'rgba(255, 255, 255, 0.5)',
                transition: { duration: 0.15, ease: 'easeOut' },
              }}
              whileTap={{
                scale: 0.95,
                transition: { duration: 0.15 },
              }}
            >
              <motion.img
                src={gunnerImg}
                alt="Gunner"
                className="absolute inset-0 w-full h-full object-cover"
                onHoverStart={() => setGunnerHover(true)}
                onHoverEnd={() => setGunnerHover(false)}
                animate={
                  gunnerHover
                    ? {
                        scale: 1.1,
                        x: [0, -2, 2, -2, 0, -2, 2, -2, 0],
                        y: [0, 1, 0, -1, 0, 1, 0],
                        filter: 'brightness(1.2) contrast(1.1)',
                      }
                    : {
                        scale: 1,
                        x: 0,
                        y: 0,
                        filter: 'brightness(1) contrast(1)',
                      }
                }
                transition={
                  gunnerHover
                    ? {
                        scale: { duration: 0.2 },
                        x: { duration: 3, ease: 'easeInOut', repeat: Infinity },
                        y: { duration: 2, ease: 'easeInOut', repeat: Infinity },
                        filter: { duration: 0.2 },
                      }
                    : {
                        duration: 0.3,
                      }
                }
              />
            </motion.div>
          </motion.div>
        </motion.div>

        {/* PVE Button */}
        <motion.button
          onClick={handlePveClick}
          className="w-full aspect-[335/76] relative rounded-xl cursor-pointer"
          initial={{ opacity: 0, x: -100 }}
          animate={
            imagesLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }
          }
          transition={{
            duration: 0.6,
            ease: 'easeOut',
            type: 'spring',
            stiffness: 100,
          }}
          whileHover={{
            scale: 1.01,
            y: -5,
            boxShadow: '0 10px 30px rgba(255, 200, 0, 0.5)',
            transition: { duration: 0.2, ease: 'easeOut' },
          }}
          whileTap={{
            scale: 0.95,
            rotate: [0, -2, 2, -2, 0],
            transition: { duration: 0.2 },
          }}
        >
          <motion.img
            src={pveButton}
            alt="PVE"
            className="w-full h-full object-contain"
            animate={{
              filter: [
                'brightness(1) drop-shadow(0 0 0px rgba(255, 200, 0, 0))',
                'brightness(1.03) drop-shadow(0 0 8px rgba(255, 200, 0, 0.3))',
                'brightness(1) drop-shadow(0 0 0px rgba(255, 200, 0, 0))',
              ],
            }}
            transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </motion.button>

        {/* PVP Button */}
        <motion.button
          onClick={handlePvpClick}
          className="w-full aspect-[335/76] relative rounded-xl cursor-pointer"
          initial={{ opacity: 0, x: -100 }}
          animate={
            imagesLoaded ? { opacity: 1, x: 0 } : { opacity: 0, x: -100 }
          }
          transition={{
            duration: 0.6,
            ease: 'easeOut',
            type: 'spring',
            stiffness: 100,
          }}
          whileHover={{
            scale: 1.01,
            y: -5,
            boxShadow: '0 10px 30px rgba(94, 255, 0, 0.5)',
            transition: { duration: 0.2, ease: 'easeOut' },
          }}
          whileTap={{
            scale: 0.95,
            rotate: [0, -2, 2, -2, 0],
            transition: { duration: 0.2 },
          }}
        >
          <motion.img
            src={pvpButton}
            alt="PVP"
            className="w-full h-full object-contain"
            animate={{
              filter: [
                'brightness(1) drop-shadow(0 0 0px rgba(255, 200, 0, 0))',
                'brightness(1.03) drop-shadow(0 0 8px rgba(255, 200, 0, 0.3))',
                'brightness(1) drop-shadow(0 0 0px rgba(255, 200, 0, 0))',
              ],
            }}
            transition={{
              duration: 3.5,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        </motion.button>
      </motion.div>

      {/* Unity Game Modal */}
      <UnityGameModal
        isOpen={isUnityGameOpen}
        gameMode={selectedGameMode}
        onClose={() => setIsUnityGameOpen(false)}
        onBackHome={onRefreshContent}
      />

      {/* Soldier Upgrade Modal */}
      <SoldierUpgradeModal
        isOpen={isSoldierModalOpen}
        initialSoldier={selectedSoldier}
        onClose={() => setIsSoldierModalOpen(false)}
        onUpgrade={handleSoldierUpgrade}
        onUpgradeSuccess={refetchDefenderData}
      />
    </div>
  );
};
